// Tipos básicos de domínio
export * from "./api-response.types";

export interface Cliente {
  id: string;
  nome: string;
  email: string;
  telefone?: string;
  cnpj?: string;
  endereco?: string;
  /** Honorário em pontos percentuais (8 = 8%). Vazio vale o padrão de 8%. */
  percentHonorarios?: number | null;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Cnae {
  id: string;
  codigo: string; // Ex: 62.01-5-01
  descricao: string;
}

export interface FornecedorCnae {
  id: string;
  fornecedorId: string;
  cnaeId: string;
  cnae?: Cnae;
  principal: boolean; // Se é o CNAE principal
}

export interface Fornecedor {
  id: string;
  nome: string; // Nome Fantasia
  cnpj?: string;
  email?: string;
  /** E-mail da Receita Federal. É o padrão do convite de cotação. */
  emailCnpj?: string;
  telefone?: string;

  // Dados da Receita Federal
  razaoSocial?: string;
  /**
   * Fantasia real, nula quando a empresa não tem uma registrada. Não confundir
   * com `nome`, que é rótulo de exibição e cai na razão social quando falta.
   */
  nomeFantasia?: string | null;
  dataAbertura?: string;
  situacao?: string;
  tipo?: string; // MATRIZ, FILIAL
  porte?: string;
  naturezaJuridica?: string;
  naturezaJuridicaDesc?: string;
  capitalSocial?: number;

  // Endereço completo
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;

  // QSA (Quadro Societário e Administradores)
  qsa?: Array<{
    nome: string;
    qual: string;
    nome_rep_legal?: string;
    qual_rep_legal?: string;
  }>;

  // Relacionamento com CNAEs
  cnaes?: FornecedorCnae[];

  // Dados do Wrike
  wrikeId?: string;

  // Campos legados (manter compatibilidade)
  categoria?: string;
  endereco?: string;
  cnaePrincipal?: string; // CNAE principal para exibição rápida

  createdAt?: Date;
  updatedAt?: Date;
}

export interface Planilha {
  id: string;
  clienteId: string;
  clienteNome: string;
  /**
   * Honorário do cliente em pontos percentuais (8 = 8%). Para usar em conta,
   * passe por `taxaDeHonorarios`, que converte para fração.
   */
  percentHonorarios?: number;
  evento: string;
  dataInicio: string; // Novo campo: data de início (yyyy-MM-dd)
  dataFim?: string | null; // Novo campo: data de fim (yyyy-MM-dd), opcional
  localEvento?: string | null; // Novo campo: local do evento, opcional
  ufEvento?: string | null; // Novo campo: UF do evento, opcional
  status: PlanilhaStatus;
  totalGeral: number;
  pit?: string;
  numeroOC?: string;
  linkWrike?: string;
  linkProjeto?: string;
  breakdown?: {
    equipamentos?: number;
    materiais?: number;
    servicos?: number;
    // Percentual do total de complementares por faixa. Ausente em planilha
    // gravada antes do campo existir, que segue valendo pelo valor absoluto.
    percentEquipamentos?: number | null;
    percentMateriais?: number | null;
    percentServicos?: number | null;
  };
  itens: ItemPlanilha[];
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  lockInfo?: { usuarioNome: string; expiraEm: string } | null;
  // Campo legado para compatibilidade (será removido)
  data?: string;
}

// Gravado em slug na coluna `status`, espelhando StatusPlanilha da API. O
// rótulo exibido fica em utils/planilhaStatus — assim renomear "FACITE Gerada"
// na tela não exige tocar no banco.
export enum PlanilhaStatus {
  PENDENTE = "pendente",
  EM_ANDAMENTO = "em_andamento",
  ENVIADO_WRIKE = "enviado_wrike",
  FACITE_GERADA = "facite_gerada",
  CONCLUIDO = "concluido",
  FINALIZADO = "finalizado",
  CANCELADO = "cancelado",
}

export enum TipoItem {
  GRUPO = "grupo",
  SUBGRUPO = "subgrupo",
  ITEM = "item",
  FORNECEDOR = "fornecedor",
  ITEM_ACESSORIA = "item_acessoria",
  DESCRITIVO_ACESSORIA = "descritivo_acessoria",
  DESCRITIVO_TAXAS = "descritivo_taxas",
  ITEM_TAXAS = "item_taxas", // Itens amarelos do subgrupo "TAXAS E SEGUROS"
  // Subgrupo TRANSPORTES: a linha amarela tem valor como em TAXAS, mas o
  // documento é por fornecedor, então quem recebe o anexo é a linha branca.
  ITEM_TRANSPORTE = "item_transporte",
  DESCRITIVO_TRANSPORTE = "descritivo_transporte",
}

/** Linhas brancas que recebem anexo por fornecedor. */
export const TIPOS_LINHA_COM_ANEXO: TipoItem[] = [
  TipoItem.DESCRITIVO_TRANSPORTE,
  TipoItem.DESCRITIVO_ACESSORIA,
];

/** Linhas amarelas de valor manual, sem cotação por fornecedor. */
export const TIPOS_ITEM_SEM_COTACAO: TipoItem[] = [
  TipoItem.ITEM_TAXAS,
  TipoItem.ITEM_TRANSPORTE,
];

export enum StatusOrcamento {
  AGUARDANDO_ENVIO = "aguardando_envio",
  ENVIADA = "enviada",
  RESPONDIDA = "respondida",
  REENVIADA = "reenviada",
  REJEITADA = "rejeitada",
}

// ── Envio em bloco de solicitação de orçamento ──────────────────────────────
// O modal "Solicitar Orçamento" precisa enxergar TODOS os itens de cada
// fornecedor, e não só os pendentes. Incluir ou excluir um item invalida a
// cotação inteira dele, e a API só consolida tudo em um único orçamento — um
// link, um PDF — quando recebe a lista completa de itens: mandar apenas o item
// novo cria um segundo orçamento paralelo para o mesmo fornecedor.

/** "todos" reenvia o pedido completo; "pendentes" envia só o que nunca saiu. */
export type ModoEnvioFornecedor = "todos" | "pendentes";

export interface ItemEnvioFornecedor {
  itemId?: string;
  numero: string;
  nome: string;
  status: StatusOrcamento;
  /** Solicitação ainda não enviada a este fornecedor — primeiro envio. */
  pendente: boolean;
}

export interface FornecedorEnvioBloco {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  itens: ItemEnvioFornecedor[];
}

export interface ItemPlanilha {
  id?: string;
  tipo: TipoItem;
  numero?: string;
  item: string;
  itemPaiOrdem?: number;
  descritivo?: string;
  quantidade?: number;
  diaria?: number;
  unitario?: number;
  subtotal?: number;
  total?: number;
  status?: StatusOrcamento;
  observacoes?: string;
  fornecedorId?: string;
  fornecedor?: FornecedorData; // Dados completos do fornecedor
  menorPreco?: boolean;
  orcamentoPdfUrl?: string; // URL do PDF do orçamento (para linhas de fornecedor)
  orcamentoPdfRejeicaoUrl?: string; // URL do PDF de rejeição do orçamento
  // Lembrete ao fornecedor (item 3.1) — presentes nas linhas de FORNECEDOR
  orcamentoId?: string;
  ultimoLembreteEm?: string;
  // Visto por último (item 3.2) — presentes nas linhas de FORNECEDOR
  ultimoAcessoEm?: string;
  totalAcessos?: number;
  // Campos de auditoria da solicitação
  solicitadoPorId?: string;
  solicitadoPorNome?: string;
  solicitadoPorEmail?: string;
  /**
   * Quando o fornecedor FINALIZOU a proposta. Nulo enquanto ele apenas salvou
   * etapas: é o que separa uma cotação entregue de um preenchimento
   * abandonado no meio do caminho.
   */
  dataResposta?: string | null;
  fornecedores?: Array<{
    id: string; // itemFornecedorId
    fornecedorId: string;
    fornecedor?: {
      id: string;
      nome: string;
      email: string;
      telefone?: string;
      cnpj?: string;
      inscricaoEstadual?: string | null;
      endereco?: string;
      cidade?: string;
      estado?: string;
      cep?: string;
    };
    status: string;
    unitario?: number;
    observacoes?: string;
    orcamento?: {
      id: string;
      numero: string;
      dataEnvio: string;
      valorTotal: number;
      pdfUrl: string;
      pdfRejeicaoUrl?: string;
      status?: string;
      ultimoLembreteEm?: string;
      totalLembretes?: number;
      ultimoAcessoEm?: string;
      totalAcessos?: number;
    };
    // Campos legados (mantidos para compatibilidade)
    fornecedorNome?: string;
    fornecedorEmail?: string;
    fornecedorTelefone?: string;
    fornecedorCNPJ?: string;
    orcamentoPdfUrl?: string;
    orcamentoPdfRejeicaoUrl?: string;
    // Lembrete ao fornecedor (item 3.1) — vindos "achatados" do endpoint getById
    orcamentoId?: string;
    ultimoLembreteEm?: string;
    // Visto por último (item 3.2) — "achatados" do getById
    ultimoAcessoEm?: string;
    totalAcessos?: number;
    // Campos de auditoria da solicitação
    solicitadoPorId?: string;
    solicitadoPorNome?: string;
    solicitadoPorEmail?: string;
    /** Preenchida só na finalização. Ver ItemPlanilha.dataResposta. */
    dataResposta?: string | null;
  }>;
}

// Dados do fornecedor salvos no item da planilha
export interface FornecedorData {
  id: string;
  nome: string;
  email: string;
  cnpj?: string;
  telefone?: string;
  cnaePrincipal?: string;
}

// Orçamento - representa o envio para um fornecedor
export interface Orcamento {
  id: string;
  planilhaId: string;
  fornecedorId: string;
  fornecedor: FornecedorData;
  planilha?: Planilha; // Incluído quando busca por token
  token: string; // UUID para acesso via link
  status: StatusOrcamento;
  itens: ItemOrcamento[]; // Itens que este fornecedor deve orçar
  dataEnvio?: Date;
  dataResposta?: Date;
  observacoes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Item específico de um orçamento
export interface ItemOrcamento {
  itemFornecedorId?: string; // ID da linha itens_fornecedores
  itemId?: string; // ID do item na planilha
  numero?: string; // mantido para compatibilidade/exibição
  item?: string;
  descritivo?: string;
  quantidade?: number;
  diaria?: number;
  unitario?: number; // Preenchido pelo fornecedor
  observacoes?: string; // Observações do fornecedor
}

// DTOs para criação
export interface CreateClienteDTO {
  nome: string;
  email: string;
  telefone?: string;
  cnpj?: string;
  endereco?: string;
  /** Pontos percentuais (8 = 8%). */
  percentHonorarios?: number;
}

export interface CreateFornecedorDTO {
  nome: string;
  email: string;
  telefone?: string;
  cnpj?: string;
  categoria?: string;
  endereco?: string;
  /**
   * Fantasia informada à mão. String vazia é valor válido: significa que a
   * empresa não tem fantasia, e é o que faz o FACITE imprimir a marca de
   * ausência em vez de tentar deduzir pelo nome.
   */
  nomeFantasia?: string;
}

export interface CreatePlanilhaDTO {
  clienteId: string;
  clienteNome: string;
  evento: string;
  dataInicio: string; // Obrigatório: data de início (yyyy-MM-dd)
  dataFim?: string | null; // Opcional: data de fim (yyyy-MM-dd)
  localEvento?: string | null; // Opcional: local do evento
  ufEvento?: string | null; // Opcional: UF do evento
  pit?: string;
  numeroOC?: string;
  linkWrike?: string;
  linkProjeto?: string;
}

export interface CreateOrcamentoDTO {
  planilhaId: string;
  fornecedorId: string;
}

export interface EnviarOrcamentosDTO {
  planilhaId: string;
  fornecedorIds: string[]; // Fornecedores únicos da planilha
}

export interface ResponderOrcamentoDTO {
  token: string;
  itens: ItemOrcamento[];
  observacoes?: string;
}

// DTOs para atualização
export type UpdateClienteDTO = Partial<CreateClienteDTO>;
export type UpdateFornecedorDTO = Partial<CreateFornecedorDTO>;
/**
 * Planilha para salvar, mais os campos que só existem no salvamento.
 *
 * Eles não vêm no GET e não fazem parte de `Planilha`; declarados aqui para o
 * contrato ficar visível em vez de depender do objeto passar sem checagem.
 */
export type UpdatePlanilhaDTO = Partial<Planilha> & {
  /** Autoriza o backend a invalidar cotações afetadas por campos críticos. */
  confirmarResetStatus?: boolean;
  /**
   * Ids de vínculo item_fornecedor removidos nesta edição.
   *
   * Lista vazia é diferente de ausente: ausente faz o servidor voltar à regra
   * antiga, de apagar todo vínculo que faltar no payload.
   */
  fornecedoresRemovidos?: string[];
};

// Tipos para paginação
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}
